import { PaymentDto } from 'api/billing';
import { Button } from 'components/ui/Button';
import { CardTableDisplay } from 'components/display/Card/CardTable/CardTableDisplay';
import { CardTableColumn } from 'components/display/Card/CardTable/types';
import { EyeIcon } from 'components/icons/Icons';
import { Sizes } from 'enums/sizes';
import { useDialog } from 'hooks/useDialog';
import { Calendar } from 'lucide-react';
import { useCallback } from 'react';
import { useContractBillingState } from './ContractBillingContext';
import { PaymentScheduleDetails } from './PaymentScheduleDetails';

const formatDate = (date: Date): string => {
  return new Date(date).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const columns: CardTableColumn<PaymentDto>[] = [
  {
    key: 'scheduledDate',
    label: 'Dates de prélèvement',
    width: 'col-span-3',
    render: (payment) => (
      <span className="text-sm text-gray-700">{formatDate(payment.scheduledDate)}</span>
    )
  },
  {
    key: 'clientPrice',
    label: 'Montant de l\'échéance',
    width: 'col-span-2',
    render: (payment) => (
      <span className="text-sm font-medium text-gray-900">
        {payment.clientPrice.toFixed(2)} €
      </span>
    )
  },
  {
    key: 'dueDate',
    label: 'Date de prélèvement',
    width: 'col-span-2',
    render: (payment) => (
      <span className="text-sm text-gray-700">{formatDate(payment.dueDate)}</span>
    )
  },
  {
    key: 'status',
    label: 'Statut',
    width: 'col-span-4',
    render: (payment) => (
      <span className={`text-sm ${payment.isPaid ? 'text-gray-700' : 'text-orange-600'}`}>
        {payment.status}
      </span>
    )
  }
];

export function PaymentScheduleTable() {
  const { payments } = useContractBillingState()
  const { openDialog } = useDialog()

  const seeDetails = useCallback((payment: PaymentDto) => {
    openDialog({
      title: "Details",
      size: Sizes['5xl']
    },
      <PaymentScheduleDetails payment={payment} />
    )
  }, [openDialog])

  return (
    <div className="p-6">
      <CardTableDisplay
        title="Échéancier des paiements"
        icon={Calendar}
        iconColor="text-primary"
        columns={columns}
        data={payments}
        actions={(payment) => (
          <div className="flex gap-2">
            <Button color='slate' round onClick={() => seeDetails(payment)}>
              <EyeIcon />
            </Button>
          </div>
        )}
        emptyStateMessage="Aucun paiement disponible"
        rowClassName={(payment) => payment.isPaid ? '' : 'bg-orange-50/30'}
      />

      <div className="mt-4 flex justify-end items-center gap-3">
        <span className="text-sm text-gray-600">Période</span>
        <select className="px-3 py-2 border rounded-lg text-sm bg-white">
          <option>12 derniers mois</option>
          <option>6 derniers mois</option>
          <option>Tout</option>
        </select>
      </div>
    </div>
  );
}
